import React, { useMemo, useState, useRef, useCallback } from 'react';
import './ActivityGrid.css';

// Activity session type (simplified from activityApi)
interface ActivitySession {
  session_id: string;
  session_type: 'Interview' | 'GD' | 'Coding';
  status: 'completed' | 'inuse' | 'created' | 'abandoned';
  created_at: string;
  updated_at: string;
}

interface ActivityBarProps {
  consistentDays?: number;
  data?: number[]; // Legacy: Array of activity levels (0-2) for each day
  sessions?: ActivitySession[]; // New: Actual activity sessions from API
  onDateClick?: (date: Date) => void; // Callback when a dot is clicked
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ActivityBar: React.FC<ActivityBarProps> = ({
  consistentDays,
  data,
  sessions,
  onDateClick,
}) => {
  const currentDate = new Date();
  const currentMonthIndex = currentDate.getMonth();
  
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(currentMonthIndex);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get days in a month
  const getDaysInMonth = (monthIndex: number): number => {
    const year = currentDate.getFullYear();
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  // Process sessions into daily activity data
  const sessionsByDay = useMemo(() => {
    if (!sessions || sessions.length === 0) return {};
    
    const dayMap: { [key: string]: { completed: number; total: number } } = {};
    
    sessions.forEach(session => {
      const date = new Date(session.updated_at || session.created_at);
      // Create key as "year-month-day"
      const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      
      if (!dayMap[dayKey]) {
        dayMap[dayKey] = { completed: 0, total: 0 };
      }
      
      dayMap[dayKey].total += 1;
      if (session.status === 'completed') {
        dayMap[dayKey].completed += 1;
      }
    });
    
    return dayMap;
  }, [sessions]);

  // Generate activity data for each month based on real sessions
  const monthlyData = useMemo(() => {
    const dataMap: { [key: number]: { level: number; interviews: number; completed: number }[] } = {};
    const year = currentDate.getFullYear();
    
    for (let m = 0; m <= currentMonthIndex; m++) {
      const daysInMonth = getDaysInMonth(m);
      dataMap[m] = Array.from({ length: daysInMonth }, (_, dayIndex) => {
        const dayKey = `${year}-${m}-${dayIndex + 1}`;
        const dayData = sessionsByDay[dayKey];
        
        if (dayData) {
          // Determine level based on completed sessions
          let level = 0;
          if (dayData.completed >= 3) {
            level = 2; // High activity (3+ completed)
          } else if (dayData.completed >= 1) {
            level = 1; // Some activity (1-2 completed)
          }
          return {
            level,
            interviews: dayData.total,
            completed: dayData.completed
          };
        }
        
        return { level: 0, interviews: 0, completed: 0 };
      });
    }
    
    return dataMap;
  }, [currentMonthIndex, sessionsByDay]);

  // Calculate consistent days (days with at least 1 completed session)
  const calculatedConsistentDays = useMemo(() => {
    if (consistentDays !== undefined) return consistentDays;
    
    let count = 0;
    Object.values(sessionsByDay).forEach(day => {
      if (day.completed > 0) count++;
    });
    return count;
  }, [consistentDays, sessionsByDay]);

  // Use provided data or generated data from sessions
  const activityData = useMemo(() => {
    if (data) {
      return data.map(level => ({
        level,
        interviews: level === 0 ? 0 : level === 1 ? 1 : 3,
        completed: level === 0 ? 0 : level === 1 ? 1 : 3
      }));
    }
    return monthlyData[selectedMonthIndex] || [];
  }, [data, monthlyData, selectedMonthIndex]);

  const selectedMonth = MONTHS[selectedMonthIndex];
  const previousMonthIndex = selectedMonthIndex > 0 ? selectedMonthIndex - 1 : null;
  const previousMonth = previousMonthIndex !== null ? MONTHS[previousMonthIndex] : null;
  
  // Show next month on right only when viewing a past month
  const isViewingPastMonth = selectedMonthIndex < currentMonthIndex;
  const nextMonthIndex = selectedMonthIndex + 1;
  const nextMonth = isViewingPastMonth ? MONTHS[nextMonthIndex] : null;

  const getActivityClass = (level: number): string => {
    switch (level) {
      case 0: return 'activity-dot--none';
      case 1: return 'activity-dot--some';
      case 2: return 'activity-dot--complete';
      default: return 'activity-dot--none';
    }
  };

  const handleMouseEnter = useCallback((index: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setActiveIndex(null);
    }, 1000);
  }, []);

  const handlePreviousMonth = () => {
    if (previousMonthIndex !== null) {
      setSelectedMonthIndex(previousMonthIndex);
      setActiveIndex(null);
    }
  };

  const handleNextMonth = () => {
    if (nextMonthIndex <= currentMonthIndex) {
      setSelectedMonthIndex(nextMonthIndex);
      setActiveIndex(null);
    }
  };

  const handleMonthClick = (monthIndex: number) => {
    if (monthIndex >= 0 && monthIndex <= currentMonthIndex) {
      setSelectedMonthIndex(monthIndex);
      setActiveIndex(null);
    }
  };

  const handleDotClick = useCallback((dayIndex: number) => {
    if (!onDateClick) return;
    const year = currentDate.getFullYear();
    const clickedDate = new Date(year, selectedMonthIndex, dayIndex + 1);
    onDateClick(clickedDate);
  }, [onDateClick, selectedMonthIndex, currentDate]);

  const canGoPrevious = previousMonthIndex !== null;
  const canGoNext = selectedMonthIndex < currentMonthIndex;

  return (
    <div className="activity-bar">
      {/* Leftmost arrow */}
      <button 
        className={`activity-bar__arrow activity-bar__arrow--left ${!canGoPrevious ? 'activity-bar__arrow--disabled' : ''}`}
        onClick={handlePreviousMonth}
        disabled={!canGoPrevious}
        aria-label="Previous month"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>

      <span className="activity-bar__divider"></span>

      {/* Left section: Previous month + Current month */}
      <div className="activity-bar__nav-left">
        {previousMonth && (
          <>
            <span 
              className="activity-bar__month activity-bar__month--secondary"
              onClick={() => handleMonthClick(previousMonthIndex!)}
            >
              {previousMonth}
            </span>
            <span className="activity-bar__separator">|</span>
          </>
        )}
        <span className="activity-bar__month activity-bar__month--primary">{selectedMonth}</span>
      </div>

      {/* Center: Dots */}
      <div className="activity-bar__dots">
        {activityData.map((item, index) => (
          <div 
            key={index} 
            className={`activity-dot-wrapper ${activeIndex === index ? 'active' : ''}`}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleDotClick(index)}
            style={{ cursor: onDateClick ? 'pointer' : 'default' }}
          >
            <div className={`activity-dot ${getActivityClass(item.level)}`} />
            <span className="activity-dot__label">
              Day {index + 1}: {item.completed} interview{item.completed !== 1 ? 's' : ''}
            </span>
          </div>
        ))}
      </div>

      {/* Right section: Consistency + Next month (when viewing past) */}
      <div className="activity-bar__nav-right">
        <span className="activity-bar__stat">{calculatedConsistentDays} day(s) consistent</span>
        {nextMonth && (
          <>
            <span className="activity-bar__separator">|</span>
            <span 
              className="activity-bar__month activity-bar__month--secondary"
              onClick={() => handleMonthClick(nextMonthIndex)}
            >
              {nextMonth}
            </span>
          </>
        )}
      </div>

      <span className="activity-bar__divider"></span>

      {/* Rightmost arrow */}
      <button 
        className={`activity-bar__arrow activity-bar__arrow--right ${!canGoNext ? 'activity-bar__arrow--disabled' : ''}`}
        onClick={handleNextMonth}
        disabled={!canGoNext}
        aria-label="Next month"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    </div>
  );
};

export default ActivityBar;
